import { captureException, setUser } from '@sentry/nextjs'
import axios from 'axios'
import { useSession } from 'next-auth/react'
import { ErrorInfo, ReactNode, useEffect } from 'react'
import { ErrorBoundary as ReactErrorBoundary } from 'react-error-boundary'

import type { FallbackProps } from 'react-error-boundary'

import Error404 from '@/pages/404'
import Error500 from '@/pages/500'
import { usersMeRetrieve } from '@api'

const onError = (error: Error, info: ErrorInfo) => {
  // NOTE: componentStackを渡さないと、Hooks順序違反のようにReact内部でthrowされる
  //       エラーがSentry上でどのコンポーネント由来か特定できなくなる。
  captureException(error, {
    contexts: { react: { componentStack: info.componentStack } },
  })

  if (process.env.NODE_ENV === 'development') {
    console.error('ErrorBoundary: ', error)
  }
}

const ErrorFallback = ({ error, resetErrorBoundary }: FallbackProps) => {
  if (axios.isAxiosError(error)) {
    switch (error.response?.status) {
      case 404:
        return <Error404 resetErrorBoundary={resetErrorBoundary} />
      default:
        return <Error500 resetErrorBoundary={resetErrorBoundary} />
    }
  }
  return <Error500 resetErrorBoundary={resetErrorBoundary} />
}

export const ErrorBoundary = ({ children }: { children: ReactNode }) => {
  const { status } = useSession()

  useEffect(() => {
    if (status === 'authenticated') {
      usersMeRetrieve()
        .then((user) => {
          setUser({
            id: user.users_id,
            email: user.email,
            username: user.username,
          })
        })
        .catch(console.warn)
    } else {
      setUser(null)
    }
  }, [status])

  return (
    <ReactErrorBoundary FallbackComponent={ErrorFallback} onError={onError}>
      {children}
    </ReactErrorBoundary>
  )
}
